import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { Calendar, User, Home, MapPin, Phone, Users, Bed, CreditCard, Clock, Globe, MessageSquare, Baby as BabyIcon, Dog } from 'lucide-react';
import { House, Reservation } from '../../services/types';
import moment from 'moment';
import { useUserData } from '../../hooks';

interface CreateReservationModalProps {
    show: boolean;
    onHide: () => void;
    houses: House[];
    prefilledDates?: {
        checkIn: Date;
        checkOut: Date;
    };
    reservation?: Reservation | null; // For editing existing reservation
    extractedData?: any; // Data extracted from PDF
    onSubmit: (reservationData: any, reservationId?: string) => Promise<void>;
}

// Convert 24-hour time to 12-hour AM/PM format for display
const formatTimeTo12Hour = (time24: string) => {
    const [hour24, minute] = time24.split(':').map(Number);
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 || 12; // Convert 0 to 12 for midnight
    return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
};

// Generate time options in 30-minute intervals (stored as HH:mm:ss, displayed as 12-hour AM/PM)
const generateTimeOptions = () => {
    const times: Array<{ value: string; label: string }> = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
            const hourStr = hour.toString().padStart(2, '0');
            const minuteStr = minute.toString().padStart(2, '0');
            const value = `${hourStr}:${minuteStr}:00`;
            const label = formatTimeTo12Hour(`${hourStr}:${minuteStr}`);
            times.push({ value, label });
        }
    }
    return times;
};

const CreateReservationModal: React.FC<CreateReservationModalProps> = ({
    show,
    onHide,
    houses,
    prefilledDates,
    reservation,
    extractedData,
    onSubmit,
}) => {
    const { canEdit, userID } = useUserData();
    const timeOptions = generateTimeOptions();

    const [formData, setFormData] = useState({
        house_id: '',
        check_in_date: '',
        check_out_date: '',
        name_first_guess: '',
        contact: '',
        rooms: 1,
        platform: '',
        id_reservation_platform: '',
        reservation_state: 'pending',
        nationality: '',
        observations: '',
        baby: false,
        pets: false,
        number_of_guests: 1,
        check_in_hour: '',
        check_out_hour: '',
        deposit_number: '',
        is_paid: false,
    });

    const [submitting, setSubmitting] = useState(false);
    const isEditMode = !!reservation;

    // Load reservation data for editing or prefill dates
    useEffect(() => {
        if (reservation) {
            // Edit mode - load all reservation data
            setFormData({
                house_id: reservation.house_id,
                check_in_date: moment(reservation.check_in_date).format('YYYY-MM-DD'),
                check_out_date: moment(reservation.check_out_date).format('YYYY-MM-DD'),
                name_first_guess: reservation.name_first_guess || '',
                number_of_guests: reservation.number_of_guests || 1,
                contact: reservation.contact || '',
                rooms: reservation.rooms || 1,
                platform: reservation.platform || '',
                id_reservation_platform: reservation.id_reservation_platform || '',
                reservation_state: reservation.reservation_state || 'pending',
                nationality: reservation.nationality || '',
                observations: reservation.observations || '',
                baby: reservation.baby || false,
                pets: reservation.pets || false,
                check_in_hour: reservation.check_in_hour ? moment(reservation.check_in_hour, 'HH:mm:ss').format('HH:mm:ss') : '',
                check_out_hour: reservation.check_out_hour ? moment(reservation.check_out_hour, 'HH:mm:ss').format('HH:mm:ss') : '',
                deposit_number: reservation.deposit_number ? reservation.deposit_number.toString() : '',
                is_paid: reservation.is_paid || false,
            });
        } else if (extractedData) {
            // Create mode with extracted PDF data
            setFormData({
                house_id: extractedData.house_id || '',
                check_in_date: extractedData.check_in_date ? moment(extractedData.check_in_date).format('YYYY-MM-DD') : '',
                check_out_date: extractedData.check_out_date ? moment(extractedData.check_out_date).format('YYYY-MM-DD') : '',
                name_first_guess: extractedData.name_first_guess || extractedData.guest_name || '',
                number_of_guests: extractedData.number_of_guests || 1,
                contact: extractedData.contact || '',
                rooms: extractedData.rooms || 1,
                platform: extractedData.platform || '',
                id_reservation_platform: extractedData.id_reservation_platform || '',
                reservation_state: 'pending',
                nationality: extractedData.nationality || '',
                observations: extractedData.observations || '',
                baby: extractedData.baby || false,
                pets: extractedData.pets || false,
                check_in_hour: extractedData.check_in_hour || '',
                check_out_hour: extractedData.check_out_hour || '',
                deposit_number: extractedData.deposit_number || '',
                is_paid: extractedData.is_paid || false,
            });
        } else if (prefilledDates) {
            // Create mode with prefilled dates
            setFormData(prev => ({
                ...prev,
                check_in_date: moment(prefilledDates.checkIn).format('YYYY-MM-DD'),
                check_out_date: moment(prefilledDates.checkOut).format('YYYY-MM-DD'),
            }));
        } else {
            // Reset form for create mode
            setFormData({
                house_id: '',
                check_in_date: '',
                check_out_date: '',
                name_first_guess: '',
                contact: '',
                rooms: 1,
                platform: '',
                id_reservation_platform: '',
                reservation_state: 'pending',
                nationality: '',
                observations: '',
                baby: false,
                pets: false,
                number_of_guests: 1,
                check_in_hour: '',
                check_out_hour: '',
                deposit_number: '',
                is_paid: false,
            });
        }
    }, [reservation, prefilledDates, extractedData, show]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;

        let processedValue: any = value;

        if (name === 'rooms' || name === 'number_of_guests') {
            // Only allow digits for integer fields
            const numValue = value.replace(/\D/g, '');
            processedValue = numValue ? parseInt(numValue, 10) : '';
        } else if (name === 'deposit_number') {
            // Allow digits and decimal point for decimal fields
            const numValue = value.replace(/[^\d.]/g, '');
            processedValue = numValue ? parseFloat(numValue) : '';
        } else if (type === 'checkbox') {
            processedValue = (e.target as HTMLInputElement).checked;
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            // Prepare payload - convert empty strings to null for optional fields
            const payload = {
                ...formData,
                user_id: userID || null, // Add current user's ID
                check_in_hour: formData.check_in_hour || null,
                check_out_hour: formData.check_out_hour || null,
                nationality: formData.nationality || null,
                observations: formData.observations || null,
                deposit_number: formData.deposit_number ? parseFloat(formData.deposit_number.toString()) : null,
                platform: formData.platform || null,
                id_reservation_platform: formData.id_reservation_platform || null,
            };

            console.log('Submitting reservation with user_id:', userID);

            await onSubmit(payload, reservation?.public_id);
            handleClose();
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} reservation:`, error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormData({
            house_id: '',
            check_in_date: '',
            check_out_date: '',
            name_first_guess: '',
            contact: '',
            rooms: 1,
            number_of_guests: 1,
            platform: '',
            id_reservation_platform: '',
            baby: false,
            check_in_hour: '',
            check_out_hour: '',
            deposit_number: '',
            is_paid: false,
            nationality: '',
            observations: '',
            pets: false,
            reservation_state: 'pending'
        });
        onHide();
    };

    const nights = formData.check_in_date && formData.check_out_date
        ? moment(formData.check_out_date).diff(moment(formData.check_in_date), 'days')
        : 0;

    const isMobile = window.innerWidth < 768;

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="xl"
            scrollable
            centered={!isMobile}
        >
            <Modal.Header closeButton className="border-bottom" style={{ padding: isMobile ? '1rem' : '1.5rem', backgroundColor: '#f8f9fa' }}>
                <div className="d-flex flex-column w-100">
                    <Modal.Title style={{ fontSize: isMobile ? '1.1rem' : '1.5rem', fontWeight: 600, color: '#1f2937' }}>
                        <Calendar size={isMobile ? 20 : 28} className="me-2" style={{ marginTop: '-4px', color: '#3b82f6' }} />
                        {isEditMode ? 'Edit Reservation' : 'Create New Reservation'}
                    </Modal.Title>
                    {extractedData && !isEditMode && (
                        <div className="mt-2">
                            <small className="text-success" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontSize: '1rem' }}>✓</span>
                                Data extracted from PDF. Please review and adjust as needed.
                            </small>
                        </div>
                    )}
                </div>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body
                    style={{
                        padding: isMobile ? '1rem' : '1.5rem',
                        maxHeight: isMobile ? 'calc(100vh - 250px)' : 'calc(100vh - 300px)',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch'
                    }}
                >
                    <Row className={isMobile ? 'g-3' : 'g-4'}>
                        {/* SECTION: Property Selection */}
                        <Col xs={12}>
                            <div className="mb-3">
                                <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                                    <MapPin size={14} className="me-2" style={{ marginTop: '-2px' }} />
                                    Property Selection
                                </h6>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small">
                                        <Home size={16} className="me-2" style={{ marginTop: '-2px' }} />
                                        House <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="house_id"
                                        value={formData.house_id}
                                        onChange={handleChange}
                                        required
                                        style={{ padding: '0.6rem 0.75rem' }}
                                    >
                                        <option value="">Select a house...</option>
                                        {houses.map(house => (
                                            <option key={house.public_id} value={house.public_id}>
                                                {house.name || house.address}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </div>
                            <hr className={isMobile ? 'my-3' : 'my-4'} style={{ opacity: 0.1 }} />
                        </Col>

                        {/* SECTION: Booking Dates */}
                        <Col xs={12}>
                            <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                                <Calendar size={14} className="me-2" style={{ marginTop: '-2px' }} />
                                Booking Dates
                            </h6>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    Check-in Date <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    name="check_in_date"
                                    value={formData.check_in_date}
                                    onChange={handleChange}
                                    required
                                    min={moment().format('YYYY-MM-DD')}
                                    style={{ padding: '0.6rem 0.75rem' }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    Check-out Date <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    name="check_out_date"
                                    value={formData.check_out_date}
                                    onChange={handleChange}
                                    required
                                    min={formData.check_in_date || moment().format('YYYY-MM-DD')}
                                    style={{ padding: '0.6rem 0.75rem' }}
                                />
                            </Form.Group>
                        </Col>

                        {nights > 0 && (
                            <Col xs={12}>
                                <div className="alert mb-0" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', color: '#1e40af', borderRadius: '8px', padding: '0.75rem 1rem' }}>
                                    <strong style={{ fontSize: '1.1rem' }}>{nights}</strong> night{nights !== 1 ? 's' : ''} total
                                </div>
                            </Col>
                        )}

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    <Clock size={16} className="me-2" style={{ marginTop: '-2px' }} />
                                    Check-in Hour
                                </Form.Label>
                                <Form.Select
                                    name="check_in_hour"
                                    value={formData.check_in_hour}
                                    onChange={handleChange}
                                    style={{ padding: '0.6rem 0.75rem' }}
                                >
                                    <option value="">Select check-in time...</option>
                                    {timeOptions.map(({ value, label }) => (
                                        <option key={`checkin-${value}`} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    <Clock size={16} className="me-2" style={{ marginTop: '-2px' }} />
                                    Check-out Hour
                                </Form.Label>
                                <Form.Select
                                    name="check_out_hour"
                                    value={formData.check_out_hour}
                                    onChange={handleChange}
                                    style={{ padding: '0.6rem 0.75rem' }}
                                >
                                    <option value="">Select check-out time...</option>
                                    {timeOptions.map(({ value, label }) => (
                                        <option key={`checkout-${value}`} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col xs={12}>
                            <hr className={isMobile ? 'my-3' : 'my-4'} style={{ opacity: 0.1 }} />
                        </Col>

                        {/* SECTION: Guest Information */}
                        <Col xs={12}>
                            <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                                <User size={14} className="me-2" style={{ marginTop: '-2px' }} />
                                Guest Information
                            </h6>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    <User size={16} className="me-2" style={{ marginTop: '-2px' }} />
                                    Guest Name <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name_first_guess"
                                    value={formData.name_first_guess}
                                    onChange={handleChange}
                                    placeholder="Enter guest full name"
                                    required
                                    style={{ padding: '0.6rem 0.75rem' }}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    <Phone size={16} className="me-2" style={{ marginTop: '-2px' }} />
                                    Contact <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="contact"
                                    value={formData.contact}
                                    onChange={handleChange}
                                    placeholder="Phone or email"
                                    required
                                    style={{ padding: '0.6rem 0.75rem' }}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    <Globe size={16} className="me-2" style={{ marginTop: '-2px' }} />
                                    Nationality
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="nationality"
                                    value={formData.nationality}
                                    onChange={handleChange}
                                    placeholder="Guest nationality (optional)"
                                    style={{ padding: '0.6rem 0.75rem' }}
                                />
                            </Form.Group>
                        </Col>

                        <Col xs={12}>
                            <hr className={isMobile ? 'my-3' : 'my-4'} style={{ opacity: 0.1 }} />
                        </Col>

                        {/* SECTION: Accommodation Details */}
                        <Col xs={12}>
                            <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                                <Bed size={14} className="me-2" style={{ marginTop: '-2px' }} />
                                Accommodation Details
                            </h6>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    <Users size={16} className="me-2" style={{ marginTop: '-2px' }} />
                                    Number of Guests <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="number_of_guests"
                                    value={formData.number_of_guests}
                                    onChange={handleChange}
                                    placeholder="e.g., 2"
                                    pattern="[0-9]+"
                                    title="Please enter only numbers"
                                    required
                                    style={{ padding: '0.6rem 0.75rem' }}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    <Bed size={16} className="me-2" style={{ marginTop: '-2px' }} />
                                    Number of Rooms <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="rooms"
                                    value={formData.rooms}
                                    onChange={handleChange}
                                    placeholder="e.g., 1"
                                    pattern="[0-9]+"
                                    title="Please enter only numbers"
                                    required
                                    style={{ padding: '0.6rem 0.75rem' }}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    <CreditCard size={16} className="me-2" style={{ marginTop: '-2px' }} />
                                    Deposit Amount
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="deposit_number"
                                    value={formData.deposit_number}
                                    onChange={handleChange}
                                    placeholder="0.00"
                                    style={{ padding: '0.6rem 0.75rem' }}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <div className="p-3" style={{ backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <Form.Label className="fw-semibold small mb-3">Additional Requirements</Form.Label>
                                <div className="d-flex gap-4">
                                    <Form.Check
                                        type="checkbox"
                                        name="baby"
                                        id="baby-check"
                                        label={
                                            <span className="d-flex align-items-center">
                                                <BabyIcon size={16} className="me-2" />
                                                <span>Baby</span>
                                            </span>
                                        }
                                        checked={formData.baby}
                                        onChange={handleChange}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        name="pets"
                                        id="pets-check"
                                        label={
                                            <span className="d-flex align-items-center">
                                                <Dog size={16} className="me-2" />
                                                <span>Pets</span>
                                            </span>
                                        }
                                        checked={formData.pets}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </Col>

                        <Col xs={12}>
                            <hr className={isMobile ? 'my-3' : 'my-4'} style={{ opacity: 0.1 }} />
                        </Col>

                        {/* SECTION: Booking Details */}
                        <Col xs={12}>
                            <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                                <Calendar size={14} className="me-2" style={{ marginTop: '-2px' }} />
                                Booking Details
                            </h6>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    <MapPin size={16} className="me-2" style={{ marginTop: '-2px' }} />
                                    Platform
                                </Form.Label>
                                <Form.Select
                                    name="platform"
                                    value={formData.platform}
                                    onChange={handleChange}
                                    style={{ padding: '0.6rem 0.75rem' }}
                                >
                                    <option value="">Select platform...</option>
                                    <option value="Airbnb">🏠 Airbnb</option>
                                    <option value="Booking.com">🏨 Booking.com</option>
                                    <option value="Direct">📞 Direct</option>
                                    <option value="Other">➕ Other</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    <CreditCard size={16} className="me-2" style={{ marginTop: '-2px' }} />
                                    Reservation ID
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="id_reservation_platform"
                                    value={formData.id_reservation_platform}
                                    onChange={handleChange}
                                    placeholder="Platform reservation ID (optional)"
                                    style={{ padding: '0.6rem 0.75rem' }}
                                />
                                <Form.Text className="text-muted">
                                    Optional platform-specific reservation ID
                                </Form.Text>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    Reservation Status
                                </Form.Label>
                                <Form.Select
                                    name="reservation_state"
                                    value={formData.reservation_state}
                                    onChange={handleChange}
                                    style={{ padding: '0.6rem 0.75rem' }}
                                >
                                    <option value="pending">⏳ Pending</option>
                                    <option value="confirmed">✅ Confirmed</option>
                                    {isEditMode && <option value="cancelled">❌ Cancelled</option>}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col xs={12}>
                            <hr className={isMobile ? 'my-3' : 'my-4'} style={{ opacity: 0.1 }} />
                        </Col>

                        {/* SECTION: Additional Notes */}
                        <Col xs={12}>
                            <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                                <MessageSquare size={14} className="me-2" style={{ marginTop: '-2px' }} />
                                Additional Notes
                            </h6>
                        </Col>

                        <Col xs={12}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    <MessageSquare size={16} className="me-2" style={{ marginTop: '-2px' }} />
                                    Observations
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    name="observations"
                                    value={formData.observations}
                                    onChange={handleChange}
                                    rows={isMobile ? 3 : 4}
                                    placeholder="Add any special requests, notes, or important information about this reservation..."
                                    style={{ padding: '0.75rem', resize: 'vertical', minHeight: '80px' }}
                                />
                                <Form.Text className="text-muted">
                                    Optional field for special requests or important details
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="bg-light border-top" style={{ padding: isMobile ? '1rem' : '1.25rem' }}>
                    <div className={`d-flex ${isMobile ? 'flex-column-reverse' : 'gap-3'} w-100 ${isMobile ? '' : 'justify-content-end'}`} style={{ gap: isMobile ? '0.75rem' : undefined }}>
                        <Button
                            variant="light"
                            onClick={handleClose}
                            disabled={submitting}
                            className={isMobile ? 'w-100' : ''}
                            style={{
                                padding: isMobile ? '0.75rem 1rem' : '0.6rem 1.5rem',
                                fontWeight: 600,
                                fontSize: isMobile ? '0.9rem' : '0.95rem',
                                border: '2px solid #dee2e6',
                                borderRadius: '8px'
                            }}
                        >
                            Cancel
                        </Button>
                        {canEdit && (
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={submitting}
                                className={isMobile ? 'w-100' : ''}
                                style={{
                                    padding: isMobile ? '0.75rem 1rem' : '0.6rem 1.5rem',
                                    fontWeight: 600,
                                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                                    borderRadius: '8px',
                                    backgroundColor: '#0d6efd',
                                    border: 'none'
                                }}
                            >
                                {submitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        {isEditMode ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : (
                                    isEditMode ? 'Update Reservation' : 'Create Reservation'
                                )}
                            </Button>
                        )}
                    </div>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CreateReservationModal;